// hooks/useComments.ts — reactive hook for post comments.
//
// Reads the comment list for a given postId from commentStore reactively.
// Exposes service mutations so components only need this hook.
// Components must NOT import commentStore or commentsService directly.

import { useShallow } from 'zustand/react/shallow';
import { useCommentStore } from '@/core/stores/commentStore';
import { commentsService } from '@/core/services/commentsService';
import type { Comment } from '@/core/models/comment';
import type { AddCommentFields, AddReplyFields } from '@/core/services/commentsService';

export interface CommentsHook {
  /** All comments for the post (any nesting level). */
  comments: Comment[];
  /** Top-level comments (parentId === null). */
  topLevel: Comment[];
  /** Direct replies to a given comment id. */
  repliesFor: (parentId: string) => Comment[];
  /** Add a top-level comment. */
  addComment: (fields: AddCommentFields) => Promise<Comment>;
  /** Add a nested reply. */
  addReply: (fields: AddReplyFields) => Promise<Comment>;
  /** Edit an existing comment. */
  updateComment: (id: string, newBody: string, reason?: string) => Promise<Comment | null>;
  /** Soft-delete a comment (tombstone). */
  deleteComment: (id: string) => Promise<Comment | null>;
  /** Toggle the liked state of a comment (±1). */
  toggleLike: (id: string) => Promise<Comment | null>;
}

export function useComments(postId: string): CommentsHook {
  // useShallow prevents re-renders when unrelated comments change —
  // the selector returns a new array every call, so without useShallow Zustand
  // would re-render this component on every store write across the whole app.
  // PATTERN: wrap any filtering/derived-list selector with useShallow
  // (T6/T7 should copy this for notifications, messages, bookmarks, etc.).
  const comments = useCommentStore(
    useShallow((s) => s.comments.filter((c) => c.postId === postId)),
  );
  const topLevel = comments.filter((c) => c.parentId === null);

  return {
    comments,
    topLevel,
    repliesFor: (parentId) => comments.filter((c) => c.parentId === parentId),
    addComment: (fields) => commentsService.addComment(fields),
    addReply: (fields) => commentsService.addReply(fields),
    updateComment: (id, body, reason) => commentsService.updateComment(id, body, reason),
    deleteComment: (id) => commentsService.deleteComment(id),
    toggleLike: (id) => commentsService.toggleCommentLike(id),
  };
}
